import React, { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { HelmetProvider, Helmet } from 'react-helmet-async';

import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import Game from './components/Game/Game';
import { initPuzzleData, getPuzzleByDate } from './lib/db';
import FeedbackForm from './components/shared/FeedbackForm';
import { trackArchiveSelected } from './lib/analytics';
import { loadStreak } from './lib/streak';


// Lazy loaded — excluded from initial bundle
const Admin             = lazy(() => import('./components/Admin/Admin'));
const Leaderboard       = lazy(() => import('./components/Leaderboard/Leaderboard'));
const HowToPlayModal    = lazy(() => import('./components/HowToPlay/HowToPlayModal'));
const HowToPlayPage     = lazy(() => import('./components/HowToPlay/HowToPlayPage'));
const NotFound          = lazy(() => import('./components/shared/NotFound'));
const PrivacyPolicyPage  = lazy(() => import('./components/PrivacyPolicy/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./components/TermsOfService/TermsOfServicePage'));

// ── Archive date picker ───────────────────────────────────────────────────────
function ArchivePicker({ recentPuzzles, selectedDate, onSelect }) {
  const today = new Date().toISOString().split('T')[0];

  const formatLabel = (dateStr) => {
    if (dateStr === today) return 'Today';
    // Parse both dates as local midnight to avoid timezone offset issues
    const [y, m, d] = dateStr.split('-').map(Number);
    const [ty, tm, td] = today.split('-').map(Number);
    const date      = new Date(y, m - 1, d);
    const todayDate = new Date(ty, tm - 1, td);
    const diffDays  = Math.round((todayDate - date) / 86400000);
    if (diffDays === 1) return 'Yesterday';
    return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' });
  };

  const todayInList = recentPuzzles.some(p => p.publish_date === today);

  // Build the full options list — always include today even if no puzzle exists
  const options = todayInList
    ? recentPuzzles
    : [{ publish_date: today, title: 'No puzzle today' }, ...recentPuzzles];

  // Only show picker if there are past puzzles available
  if (recentPuzzles.length === 0) return null;

  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="text-xs text-dimText font-mono whitespace-nowrap">📅 Playing:</span>
      <select
        value={selectedDate}
        onChange={e => onSelect(e.target.value)}
        className="flex-1 bg-surface2 border border-border rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-accent transition-colors cursor-pointer"
      >
        {options.map(p => (
          <option key={p.publish_date} value={p.publish_date}>
            {formatLabel(p.publish_date)} — {p.title}
          </option>
        ))}
      </select>
    </div>
  );
}

// ── Main game page ────────────────────────────────────────────────────────────
function GamePage() {
  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate]   = useState(today);
  const [puzzle, setPuzzle]               = useState(null);
  const [recentPuzzles, setRecentPuzzles] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [showDifficulty, setShowDifficulty] = useState(false);

  // Load puzzle for any selected date — also handles initial mount
  const loadPuzzle = useCallback(async (dateStr, isInitial = false) => {
    setLoading(true);
    setError(null);
    if (!isInitial) setPuzzle(null);
    try {
      if (dateStr === today) {
        const { puzzle: p, recentPuzzles: recent, settings } = await initPuzzleData();
        setPuzzle(p);
        if (isInitial) {
          setRecentPuzzles(recent);
          if (settings?.show_difficulty) setShowDifficulty(settings.show_difficulty === 'true');
        }
      } else {
        const p = await getPuzzleByDate(dateStr);
        setPuzzle(p);
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [today]);

  // Initial load — fetch puzzle and settings in parallel
  useEffect(() => {
    loadPuzzle(today, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload when date changes (including back to today)
  useEffect(() => {
    loadPuzzle(selectedDate);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const handleDateSelect = (dateStr) => {
    setSelectedDate(dateStr);
    if (dateStr !== today) {
      const p = recentPuzzles.find(rp => rp.publish_date === dateStr);
      trackArchiveSelected({ puzzleDate: dateStr, puzzleTitle: p?.title || '' });
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="font-mono text-dimText tracking-widest animate-pulse">LOADING PUZZLE…</div>
    </div>
  );

  if (error) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <div className="text-4xl mb-3">⚠️</div>
        <p className="text-wrong font-mono text-sm">{error}</p>
        <p className="text-dimText text-xs mt-2">Check your Supabase connection</p>
      </div>
    </div>
  );

  return (
    <div className="relative z-10 max-w-xl mx-auto px-4 pb-4">
      <Helmet>
        <title>Hat Trick Grid — Daily Football Trivia Grid</title>
        <meta name="description" content="A daily 3×3 football trivia grid. Name the footballers who fit each row and column. New puzzle every day — can you solve it with 12 guesses?" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Hat Trick Grid",
          "url": "https://www.hattrickgrid.com",
          "description": "A daily 3×3 football trivia grid puzzle game",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://www.hattrickgrid.com/?date={date}",
            "query-input": "required name=date"
          }
        })}</script>
      </Helmet>
      {/* Archive date picker */}
      <ArchivePicker
        recentPuzzles={recentPuzzles}
        selectedDate={selectedDate}
        onSelect={handleDateSelect}
      />

      {/* Fallback notice */}
      {puzzle?.isFallback && (
        <div className="bg-surface2 border border-accent3/30 rounded-xl px-4 py-2.5 mb-3 text-xs text-accent3">
          📅 No new puzzle today — here's a classic to keep you sharp.
        </div>
      )}

      {/* No puzzle at all */}
      {!puzzle && !loading && (
        <div className="flex items-center justify-center min-h-[40vh]">
          <div className="text-center">
            <div className="text-6xl mb-4">⚽</div>
            <h2 className="font-display text-3xl text-accent mb-2">
              {selectedDate === today ? 'NO PUZZLE TODAY' : 'NO PUZZLE'}
            </h2>
            <p className="text-dimText text-sm">
              {selectedDate === today
                ? recentPuzzles.length > 0
                  ? 'Check back tomorrow — or pick a recent puzzle from the menu above.'
                  : 'Check back tomorrow for a new grid.'
                : 'No puzzle was published for this date.'}
            </p>
          </div>
        </div>
      )}

      {puzzle && <Game puzzle={puzzle} key={puzzle.id} showDifficulty={showDifficulty} />}

      <FeedbackForm />
    </div>
  );
}

// ── Nav ───────────────────────────────────────────────────────────────────────
function Nav({ onHowToPlay }) {
  const loc = useLocation();
  const { current: streakCount } = loadStreak();
  const links = [
    { to: '/',            label: 'Play'        },
    { to: '/leaderboard', label: 'Leaderboard' },
  ];
  return (
    <nav className="sticky top-0 z-40 bg-bg/80 backdrop-blur-md border-b border-border">
      <div className="max-w-xl mx-auto px-4 flex items-center h-12">
        <div className="flex items-center gap-6 flex-1">
          {links.map(l => (
            <Link
              key={l.to}
              to={l.to}
              className={`text-sm font-medium transition-colors ${
                loc.pathname === l.to ? 'text-accent' : 'text-dimText hover:text-white'
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {streakCount > 0 && (
            <div className="flex items-center gap-1 text-xs font-mono text-accent3 border border-accent3/30 rounded-full px-2.5 py-1">
              <span>{streakCount >= 7 ? '🌟' : streakCount >= 3 ? '🔥' : '⚽'}</span>
              <span>{streakCount}</span>
            </div>
          )}
          <button
            onClick={onHowToPlay}
            title="How to play"
            className="w-7 h-7 flex items-center justify-center rounded-full border border-border text-dimText hover:border-accent hover:text-accent transition-colors text-xs font-semibold font-mono"
          >?</button>
        </div>
      </div>
    </nav>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [showHowTo, setShowHowTo] = React.useState(false);

  // Show modal automatically on first ever visit
  React.useEffect(() => {
    const seen = localStorage.getItem('htg_seen_howto');
    if (!seen) {
      setShowHowTo(true);
      localStorage.setItem('htg_seen_howto', '1');
    }
  }, []);

  return (
    <HelmetProvider>
    <BrowserRouter>
      <div style={{overflowX:"hidden",width:"100%",maxWidth:"100vw"}}>
      <Routes>
        <Route path="/admin"      element={<Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="font-mono text-dimText animate-pulse">LOADING…</div></div>}><Admin /></Suspense>} />
        <Route path="/admin/test" element={<Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="font-mono text-dimText animate-pulse">LOADING…</div></div>}><Admin /></Suspense>} />
        <Route path="/*" element={
          <>
            <Nav onHowToPlay={() => setShowHowTo(true)} />
            {showHowTo && <Suspense fallback={null}><HowToPlayModal onClose={() => setShowHowTo(false)} /></Suspense>}
            <Routes>
              <Route path="/"            element={<GamePage />} />
              <Route path="/leaderboard" element={<Suspense fallback={null}><Leaderboard /></Suspense>} />
              <Route path="/how-to-play" element={<Suspense fallback={null}><HowToPlayPage /></Suspense>} />
              <Route path="/privacy" element={<Suspense fallback={null}><PrivacyPolicyPage /></Suspense>} />
              <Route path="/terms" element={<Suspense fallback={null}><TermsOfServicePage /></Suspense>} />
              <Route path="*" element={<Suspense fallback={null}><NotFound /></Suspense>} />
            </Routes>
          <footer className="border-t border-border mt-8 py-4">
              <div className="max-w-xl mx-auto px-4 flex items-center justify-between text-[11px] text-dimText font-mono">
                <span>© {new Date().getFullYear()} Hat Trick Grid</span>
                <div className="flex gap-4">
                  <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
                  <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
                  <Link to="/how-to-play" className="hover:text-white transition-colors">How to Play</Link>
                </div>
              </div>
            </footer>
          </>
        } />
      </Routes>
    </div>
    </BrowserRouter>
    </HelmetProvider>
  );
}
